import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
    CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import { Button } from '@/components/ui/button';
import { AgentTimeline } from '@/services/analytics/AnalyticsService';

interface ConversationTimelineChartProps {
  timelines: AgentTimeline[];
  isLoading: boolean;
}

const colors = [
  '#4E9CFF',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EF4444',
  '#06B6D4',
  '#F97316',
  '#84CC16',
  '#EC4899',
  '#6366F1',
];

const ConversationTimelineChart: React.FC<ConversationTimelineChartProps> = ({
  timelines,
  isLoading,
}) => {
  const [showLegend, setShowLegend] = useState(true);
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-genie-primary"></div>
      </div>
    );
  }

  if (timelines.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#64748B] text-lg">No data available</p>
          <p className="text-[#64748B] text-sm">Select a genie to view conversation trends</p>
        </div>
      </div>
    );
  }
  
  // Filter out timelines with zero total conversations
  const activeTimelines = timelines.filter(timeline => 
    timeline.data.some(point => point.conversations > 0)
  );
  
  if (activeTimelines.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#64748B] text-lg">No conversation data</p>
          <p className="text-[#64748B] text-sm">Selected genies have no conversations yet</p>
        </div>
      </div>
    );
  }

  const formatDateShort = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateLong = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Combine all timeline data into a single dataset
  const combinedData = activeTimelines.length > 0 ? activeTimelines[0].data.map((dataPoint) => {
    const point: any = {
      date: dataPoint.date,
      formattedDate: formatDateShort(dataPoint.date),
    };

    activeTimelines.forEach((timeline) => {
      const timelinePoint = timeline.data.find(d => d.date === dataPoint.date);
      point[timeline.agentName] = timelinePoint?.conversations || 0;
    });

    return point;
  }) : [];

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const originalDate = combinedData.find(d => d.formattedDate === label)?.date;
      // Filter out agents with zero conversations for this data point
      const filteredPayload = payload.filter((entry: any) => entry.value > 0);
      
      if (filteredPayload.length === 0) {
        return null;
      }
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-[#222B45] mb-2">
            {originalDate ? formatDateLong(originalDate) : label}
          </p>
          {filteredPayload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              <span className="font-medium">{entry.dataKey}:</span> {entry.value} conversations
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const toggleLine = (agentName: string) => {
    setHiddenLines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentName)) {
        newSet.delete(agentName);
      } else {
        newSet.add(agentName);
      }
      return newSet;
    });
  };

  const customLegend = (props: any) => {
    const { payload } = props;
    if (!payload) return null;

    return null; // We'll render the legend outside the chart
  };

  // Determine legend layout based on number of items
  const legendHeight = activeTimelines.length > 20 ? 'max-h-32' : 
                       activeTimelines.length > 10 ? 'max-h-24' : 
                       'max-h-16';
  
  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-2">
        <div className="text-sm font-medium text-[#222B45]">Conversation Trends</div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowLegend(!showLegend)}
          className="text-xs"
        >
          {showLegend ? (
            <><EyeOff className="h-3 w-3 mr-1" /> Hide Legend</>
          ) : (
            <><Eye className="h-3 w-3 mr-1" /> Show Legend</>
          )}
        </Button>
      </div>
      
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={combinedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5EAF0" />
            <XAxis
              dataKey="formattedDate"
              stroke="#64748B"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="#64748B"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip content={customTooltip} />
            {activeTimelines.map((timeline, index) => (
              <Line
                key={timeline.agentId}
                type="monotone"
                dataKey={timeline.agentName}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ fill: colors[index % colors.length], strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: colors[index % colors.length], strokeWidth: 2 }}
                hide={hiddenLines.has(timeline.agentName)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {showLegend && activeTimelines.length > 0 && (
        <div className={`mt-3 ${legendHeight} overflow-y-auto border-t pt-3`}>
          <div className="flex flex-wrap gap-x-3 gap-y-2 px-2">
            {activeTimelines.map((timeline, index) => (
              <button
                key={timeline.agentId}
                onClick={() => toggleLine(timeline.agentName)}
                className={`flex items-center gap-1.5 hover:opacity-80 transition-opacity text-xs ${
                  hiddenLines.has(timeline.agentName) ? 'opacity-40' : ''
                }`}
              >
                <span
                  className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <span className="text-[#64748B] truncate max-w-[150px]">{timeline.agentName}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationTimelineChart;